import React, { useState, useEffect } from 'react';
import {
  api,
  IntegrationMonitor,
  IntegrationWatcher,
  WatcherType,
  WatcherStatus,
  CreateIntegrationWatcherInput,
} from '../api';
import { ConfirmDialog, EmptyState, Field, Skeleton, StatusBadge } from './ui';
import { ActionMenu, Callout, Checkbox, Drawer, Icon, Input, RowActions, Select, Subsection } from './primitives';
import type { CalloutTone } from './primitives';

/** What each watcher type is called in the type tag and the type select. */
const watcherTypeLabel: Record<WatcherType, string> = {
  file: 'File',
  log: 'Log',
  database: 'Database',
  process: 'Process',
  http: 'HTTP',
  cron: 'Cron',
};

/**
 * Watcher statuses have no table of their own in labels.ts; siteHealth has
 * the same four values and tones (healthy/warning/critical/unknown). The
 * drawer keeps its own words for three of them.
 */
const watcherStatusLabel: Partial<Record<WatcherStatus, string>> = {
  healthy: 'Healthy',
  warning: 'Warning',
  critical: 'Critical',
};

/** The tone of the composite diagnosis follows the monitor's own status. */
function diagnosisTone(status: string): CalloutTone {
  if (status === 'healthy' || status === 'success') return 'success';
  if (status === 'failing' || status === 'failed') return 'danger';
  if (status === 'delayed' || status === 'stale' || status === 'warning') return 'warning';
  return 'info';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  environmentId: string;
  monitor: IntegrationMonitor;
  onMonitorUpdated: (updated: IntegrationMonitor) => void;
}

export const IntegrationWatchersDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  environmentId,
  monitor,
  onMonitorUpdated,
}) => {
  const [watchers, setWatchers] = useState<IntegrationWatcher[]>([]);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add / Edit form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingWatcher, setEditingWatcher] = useState<IntegrationWatcher | null>(null);
  const [watcherType, setWatcherType] = useState<WatcherType>('file');
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Type-specific form fields
  // File
  const [filePath, setFilePath] = useState('var/export/orders');
  const [fileIsDir, setFileIsDir] = useState(true);
  const [fileCondition, setFileCondition] = useState<
    'modified_within' | 'max_age' | 'file_count_max' | 'file_count_min' | 'exists' | 'not_exists' | 'size_min' | 'size_max'
  >('file_count_max');
  const [fileThreshold, setFileThreshold] = useState(50);

  // Log
  const [logPath, setLogPath] = useState('var/log/erp.log');
  const [logFailPattern, setLogFailPattern] = useState('ERROR|Exception|FAILED');
  const [logSuccPattern, setLogSuccPattern] = useState('');
  const [logMetricPattern, setLogMetricPattern] = useState('');

  // Database
  const [dbTable, setDbTable] = useState('sales_order');
  const [dbMode, setDbMode] = useState<'row_count' | 'freshness'>('row_count');
  const [dbFilterCol, setDbFilterCol] = useState('status');
  const [dbFilterVal, setDbFilterVal] = useState('pending');
  const [dbWarnThresh, setDbWarnThresh] = useState(50);
  const [dbCritThresh, setDbCritThresh] = useState(200);
  const [dbTsCol, setDbTsCol] = useState('updated_at');
  const [dbMaxAge, setDbMaxAge] = useState(60);

  // Process
  const [procPattern, setProcPattern] = useState('stock:import');
  const [procMaxInst, setProcMaxInst] = useState(1);
  const [procMaxRuntime, setProcMaxRuntime] = useState(30);

  // HTTP
  const [httpUrl, setHttpUrl] = useState('https://');
  const [httpStatus, setHttpStatus] = useState(200);
  const [httpTimeout, setHttpTimeout] = useState(5);

  // Cron
  const [cronJob, setCronJob] = useState('');
  const [cronWarnDelay, setCronWarnDelay] = useState(15);
  const [cronCritDelay, setCronCritDelay] = useState(30);

  const [submitting, setSubmitting] = useState(false);

  const loadWatchers = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listIntegrationWatchers(environmentId, monitor.id);
      setWatchers(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load watchers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadWatchers();
    }
  }, [isOpen, monitor.id]);

  const handleEvaluate = async () => {
    setEvaluating(true);
    setError(null);
    try {
      const { monitor: updatedMon } = await api.evaluateIntegrationMonitor(environmentId, monitor.id);
      onMonitorUpdated(updatedMon);
      await loadWatchers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Evaluation probe failed');
    } finally {
      setEvaluating(false);
    }
  };

  const handleToggleActive = async (w: IntegrationWatcher) => {
    try {
      const updated = await api.updateIntegrationWatcher(environmentId, monitor.id, w.id, {
        name: w.name,
        config: w.configuration,
        is_active: !w.is_active,
      });
      setWatchers(watchers.map((item) => (item.id === w.id ? updated : item)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle watcher active state');
    }
  };

  const [watcherToDelete, setWatcherToDelete] = useState<string | null>(null);
  const [deletingWatcher, setDeletingWatcher] = useState(false);
  const handleDeleteWatcher = (watcherId: string) => setWatcherToDelete(watcherId);
  const confirmDeleteWatcher = async () => {
    const watcherId = watcherToDelete;
    if (!watcherId) return;
    setDeletingWatcher(true);
    try {
      await api.deleteIntegrationWatcher(environmentId, monitor.id, watcherId);
      setWatchers(watchers.filter((w) => w.id !== watcherId));
      setWatcherToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete watcher');
    } finally {
      setDeletingWatcher(false);
    }
  };

  const openCreateForm = () => {
    setEditingWatcher(null);
    setName('');
    setWatcherType('file');
    setIsActive(true);
    setFilePath('var/export/orders');
    setFileIsDir(true);
    setFileCondition('file_count_max');
    setFileThreshold(50);
    setShowAddForm(true);
  };

  const openEditForm = (w: IntegrationWatcher) => {
    setEditingWatcher(w);
    setName(w.name);
    setWatcherType(w.watcher_type);
    setIsActive(w.is_active);

    if (w.configuration.file) {
      setFilePath(w.configuration.file.path);
      setFileIsDir(w.configuration.file.is_directory);
      setFileCondition(w.configuration.file.condition as any);
      setFileThreshold(w.configuration.file.threshold_value);
    }
    if (w.configuration.log) {
      setLogPath(w.configuration.log.file_path);
      setLogFailPattern(w.configuration.log.failure_pattern || '');
      setLogSuccPattern(w.configuration.log.success_pattern || '');
      setLogMetricPattern(w.configuration.log.metric_pattern || '');
    }
    if (w.configuration.database) {
      setDbTable(w.configuration.database.table_name);
      setDbMode(w.configuration.database.mode);
      setDbFilterCol(w.configuration.database.filter_column || '');
      setDbFilterVal(w.configuration.database.filter_value || '');
      setDbWarnThresh(w.configuration.database.warn_threshold || 50);
      setDbCritThresh(w.configuration.database.crit_threshold || 200);
      setDbTsCol(w.configuration.database.timestamp_column || 'updated_at');
      setDbMaxAge(w.configuration.database.max_age_minutes || 60);
    }
    if (w.configuration.process) {
      setProcPattern(w.configuration.process.match_pattern);
      setProcMaxInst(w.configuration.process.max_instances);
      setProcMaxRuntime(w.configuration.process.max_runtime_minutes);
    }
    if (w.configuration.http) {
      setHttpUrl(w.configuration.http.url);
      setHttpStatus(w.configuration.http.expected_status);
      setHttpTimeout(w.configuration.http.timeout_seconds);
    }
    if (w.configuration.cron) {
      setCronJob(w.configuration.cron.job_code);
      setCronWarnDelay(w.configuration.cron.warn_delay_minutes);
      setCronCritDelay(w.configuration.cron.crit_delay_minutes);
    }

    setShowAddForm(true);
  };

  const handleSaveWatcher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Watcher name is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    const config: any = {};
    switch (watcherType) {
      case 'file':
        config.file = {
          path: filePath.trim(),
          is_directory: fileIsDir,
          condition: fileCondition,
          threshold_value: Number(fileThreshold),
        };
        break;
      case 'log':
        config.log = {
          file_path: logPath.trim(),
          failure_pattern: logFailPattern.trim(),
          success_pattern: logSuccPattern.trim(),
          metric_pattern: logMetricPattern.trim(),
        };
        break;
      case 'database':
        config.database = {
          table_name: dbTable.trim(),
          mode: dbMode,
          filter_column: dbFilterCol.trim(),
          filter_value: dbFilterVal.trim(),
          warn_threshold: Number(dbWarnThresh),
          crit_threshold: Number(dbCritThresh),
          timestamp_column: dbTsCol.trim(),
          max_age_minutes: Number(dbMaxAge),
        };
        break;
      case 'process':
        config.process = {
          match_pattern: procPattern.trim(),
          max_instances: Number(procMaxInst),
          max_runtime_minutes: Number(procMaxRuntime),
        };
        break;
      case 'http':
        config.http = {
          url: httpUrl.trim(),
          expected_status: Number(httpStatus),
          timeout_seconds: Number(httpTimeout),
        };
        break;
      case 'cron':
        config.cron = {
          job_code: cronJob.trim(),
          warn_delay_minutes: Number(cronWarnDelay),
          crit_delay_minutes: Number(cronCritDelay),
        };
        break;
    }

    try {
      if (editingWatcher) {
        const updated = await api.updateIntegrationWatcher(environmentId, monitor.id, editingWatcher.id, {
          name: name.trim(),
          config,
          is_active: isActive,
        });
        setWatchers(watchers.map((w) => (w.id === updated.id ? updated : w)));
      } else {
        const input: CreateIntegrationWatcherInput = {
          name: name.trim(),
          watcher_type: watcherType,
          config,
          is_active: isActive,
        };
        const created = await api.createIntegrationWatcher(environmentId, monitor.id, input);
        setWatchers([...watchers, created]);
      }
      setShowAddForm(false);
      setEditingWatcher(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save signal watcher');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTimeAgo = (iso?: string) => {
    if (!iso) return 'Never evaluated';
    const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (!isOpen) return null;

  const closeForm = () => {
    setShowAddForm(false);
    setEditingWatcher(null);
  };

  const footer = showAddForm ? (
    <>
      <button type="button" onClick={closeForm} className="button button--secondary">
        Cancel
      </button>
      <button type="submit" disabled={submitting} className="button button--primary">
        {submitting ? 'Saving' : editingWatcher ? 'Save changes' : 'Add signal'}
      </button>
    </>
  ) : (
    <>
      <span className="dialog__footer-note">Non-invasive SSH probes · no code modification required</span>
      <button type="button" onClick={handleEvaluate} disabled={evaluating} className="button button--primary">
        <Icon name={evaluating ? 'spinner' : 'refresh'} />
        {evaluating ? 'Probing signals' : 'Probe signals'}
      </button>
    </>
  );

  const watcherForm = (
    <Subsection title={editingWatcher ? 'Edit signal watcher' : 'Add technical signal watcher'}>
      <div className="watchers-form stack">
        <div className="form-grid form-grid--2">
          <Field label="Signal name">
            <Input
              id="signal_name_input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Export directory file count"
              required
            />
          </Field>
          <Field label="Signal type">
            <Select
              id="signal_type_select"
              value={watcherType}
              disabled={!!editingWatcher}
              onChange={(e) => setWatcherType(e.target.value as WatcherType)}
            >
              <option value="file">File / directory watcher</option>
              <option value="log">Log file pattern watcher</option>
              <option value="database">Database table backlog watcher</option>
              <option value="process">CLI process watcher</option>
              <option value="http">Remote HTTP health watcher</option>
              <option value="cron">Magento cron schedule watcher</option>
            </Select>
          </Field>
        </div>

        {watcherType === 'file' && (
          <div className="form-grid form-grid--2">
            <Field label="Relative path (from Magento root)">
              <Input
                type="text"
                value={filePath}
                onChange={(e) => setFilePath(e.target.value)}
                placeholder="var/export/orders"
                className="input--mono"
                required
              />
            </Field>
            <Field label="Evaluation condition">
              <Select value={fileCondition} onChange={(e) => setFileCondition(e.target.value as any)}>
                <option value="file_count_max">Max file count in directory</option>
                <option value="max_age">Max stalled age (minutes)</option>
                <option value="modified_within">Modified within (minutes)</option>
                <option value="size_min">Min file size (bytes)</option>
                <option value="exists">File exists</option>
                <option value="not_exists">File does not exist</option>
              </Select>
            </Field>
            <Field label="Threshold value">
              <Input
                type="number"
                value={fileThreshold}
                onChange={(e) => setFileThreshold(Number(e.target.value))}
                className="input--mono"
              />
            </Field>
            <Checkbox
              id="is_dir_check"
              checked={fileIsDir}
              onChange={(e) => setFileIsDir(e.target.checked)}
              className="watchers-check"
              label="Target is a directory"
            />
          </div>
        )}

        {watcherType === 'log' && (
          <>
            <Field label="Log file path" hint="For example var/log/erp.log">
              <Input
                type="text"
                value={logPath}
                onChange={(e) => setLogPath(e.target.value)}
                className="input--mono"
                required
              />
            </Field>
            <div className="form-grid form-grid--2">
              <Field label="Failure pattern (regex)">
                <Input
                  type="text"
                  value={logFailPattern}
                  onChange={(e) => setLogFailPattern(e.target.value)}
                  placeholder="ERROR|Exception|FAILED"
                  className="input--mono"
                />
              </Field>
              <Field label="Success pattern (optional)">
                <Input
                  type="text"
                  value={logSuccPattern}
                  onChange={(e) => setLogSuccPattern(e.target.value)}
                  placeholder="Export complete"
                  className="input--mono"
                />
              </Field>
            </div>
            <Field label="Metric extraction regex (optional)" hint="For example Exported (\d+) orders">
              <Input
                type="text"
                value={logMetricPattern}
                onChange={(e) => setLogMetricPattern(e.target.value)}
                className="input--mono"
              />
            </Field>
          </>
        )}

        {watcherType === 'database' && (
          <>
            <div className="form-grid form-grid--2">
              <Field label="Table name">
                <Input
                  type="text"
                  value={dbTable}
                  onChange={(e) => setDbTable(e.target.value)}
                  placeholder="sales_order"
                  className="input--mono"
                  required
                />
              </Field>
              <Field label="Query mode">
                <Select value={dbMode} onChange={(e) => setDbMode(e.target.value as any)}>
                  <option value="row_count">Row count backlog</option>
                  <option value="freshness">Timestamp freshness</option>
                </Select>
              </Field>
            </div>
            {dbMode === 'row_count' ? (
              <div className="form-grid watchers-grid-4">
                <Field label="Filter column">
                  <Input
                    type="text"
                    value={dbFilterCol}
                    onChange={(e) => setDbFilterCol(e.target.value)}
                    placeholder="status"
                    className="input--mono"
                  />
                </Field>
                <Field label="Filter value">
                  <Input
                    type="text"
                    value={dbFilterVal}
                    onChange={(e) => setDbFilterVal(e.target.value)}
                    placeholder="pending"
                    className="input--mono"
                  />
                </Field>
                <Field label="Warning count">
                  <Input
                    type="number"
                    value={dbWarnThresh}
                    onChange={(e) => setDbWarnThresh(Number(e.target.value))}
                    className="input--mono"
                  />
                </Field>
                <Field label="Critical count">
                  <Input
                    type="number"
                    value={dbCritThresh}
                    onChange={(e) => setDbCritThresh(Number(e.target.value))}
                    className="input--mono"
                  />
                </Field>
              </div>
            ) : (
              <div className="form-grid form-grid--2">
                <Field label="Timestamp column">
                  <Input
                    type="text"
                    value={dbTsCol}
                    onChange={(e) => setDbTsCol(e.target.value)}
                    placeholder="updated_at"
                    className="input--mono"
                  />
                </Field>
                <Field label="Max age allowed (minutes)">
                  <Input
                    type="number"
                    value={dbMaxAge}
                    onChange={(e) => setDbMaxAge(Number(e.target.value))}
                    className="input--mono"
                  />
                </Field>
              </div>
            )}
          </>
        )}

        {watcherType === 'process' && (
          <>
            <Field label="CLI match pattern" hint="A regex or substring matched against ps aux">
              <Input
                type="text"
                value={procPattern}
                onChange={(e) => setProcPattern(e.target.value)}
                placeholder="e.g. stock:import:feed"
                className="input--mono"
                required
              />
            </Field>
            <div className="form-grid form-grid--2">
              <Field label="Max overlapping instances">
                <Input
                  type="number"
                  value={procMaxInst}
                  onChange={(e) => setProcMaxInst(Number(e.target.value))}
                  className="input--mono"
                />
              </Field>
              <Field label="Max runtime allowed (minutes)">
                <Input
                  type="number"
                  value={procMaxRuntime}
                  onChange={(e) => setProcMaxRuntime(Number(e.target.value))}
                  className="input--mono"
                />
              </Field>
            </div>
          </>
        )}

        {watcherType === 'http' && (
          <>
            <Field label="Endpoint health URL">
              <Input
                type="url"
                value={httpUrl}
                onChange={(e) => setHttpUrl(e.target.value)}
                placeholder="https://api.erp.partner/health"
                className="input--mono"
                required
              />
            </Field>
            <div className="form-grid form-grid--2">
              <Field label="Expected HTTP status">
                <Input
                  type="number"
                  value={httpStatus}
                  onChange={(e) => setHttpStatus(Number(e.target.value))}
                  className="input--mono"
                />
              </Field>
              <Field label="Timeout (seconds)">
                <Input
                  type="number"
                  value={httpTimeout}
                  onChange={(e) => setHttpTimeout(Number(e.target.value))}
                  className="input--mono"
                />
              </Field>
            </div>
          </>
        )}

        {watcherType === 'cron' && (
          <>
            <Field label="Cron job code" hint="The job_code in cron_schedule">
              <Input
                type="text"
                value={cronJob}
                onChange={(e) => setCronJob(e.target.value)}
                placeholder="e.g. erp_order_export_cron"
                className="input--mono"
                required
              />
            </Field>
            <div className="form-grid form-grid--2">
              <Field label="Warning delay (minutes)">
                <Input
                  type="number"
                  value={cronWarnDelay}
                  onChange={(e) => setCronWarnDelay(Number(e.target.value))}
                  className="input--mono"
                />
              </Field>
              <Field label="Critical delay (minutes)">
                <Input
                  type="number"
                  value={cronCritDelay}
                  onChange={(e) => setCronCritDelay(Number(e.target.value))}
                  className="input--mono"
                />
              </Field>
            </div>
          </>
        )}
      </div>
    </Subsection>
  );

  const watcherList = loading ? (
    <div className="watchers-loading" role="status" aria-label="Loading signals">
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} width="100%" height="56px" radius="var(--radius-md)" />
      ))}
    </div>
  ) : watchers.length === 0 ? (
    <EmptyState
      title="No signal watchers yet"
      description="No technical signals attached to this monitor yet. A watcher checks a file, log, database table, process, HTTP endpoint or cron job."
    >
      {showAddForm ? null : (
        <div className="empty-state__actions">
          <button type="button" onClick={openCreateForm} className="button button--secondary">
            Configure your first signal watcher
          </button>
        </div>
      )}
    </EmptyState>
  ) : (
    <ul className="watchers-list">
      {watchers.map((w) => (
        <li
          key={w.id}
          className={`watchers-item${w.status === 'critical' ? ' watchers-item--critical' : w.status === 'warning' ? ' watchers-item--warning' : ''}`}
        >
          <div className="watchers-item__head">
            <div className="watchers-item__title">
              <h4 className="watchers-item__name">{w.name}</h4>
              <StatusBadge kind="siteHealth" value={w.status} label={watcherStatusLabel[w.status]} />
              <span className="badge">{watcherTypeLabel[w.watcher_type] ?? w.watcher_type}</span>
              {w.is_active ? null : <StatusBadge kind="toggle" value="disabled" label="Paused" />}
            </div>
            <RowActions>
              <button type="button" onClick={() => handleToggleActive(w)} className="button button--secondary button--small">
                {w.is_active ? 'Pause watcher' : 'Resume watcher'}
              </button>
              <button type="button" onClick={() => openEditForm(w)} className="button button--secondary button--small">
                Edit
              </button>
              <ActionMenu
                label={`More actions for ${w.name}`}
                size="sm"
                items={[{ label: 'Remove signal', danger: true, onClick: () => handleDeleteWatcher(w.id) }]}
              />
            </RowActions>
          </div>
          <p className="watchers-item__message">{w.last_message || 'Waiting for first probe evaluation'}</p>
          <div className="watchers-item__meta">
            <div className="watchers-item__metrics">
              {w.last_metrics && Object.keys(w.last_metrics).length > 0 ? (
                Object.entries(w.last_metrics).map(([key, val]) => (
                  <span key={key} className="badge watchers-metric tabular-nums">
                    {key}: {String(val)}
                  </span>
                ))
              ) : (
                <span className="type-meta">No telemetry metrics</span>
              )}
            </div>
            <span className="type-meta tabular-nums">{formatTimeAgo(w.last_checked_at)}</span>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <Drawer
        open={isOpen}
        size="wide"
        onClose={onClose}
        title={
          <span className="watchers-title">
            <span>{monitor.name}</span>
            <StatusBadge kind="integrationMonitor" value={monitor.status} />
          </span>
        }
        description={
          <>
            Target: <span className="watchers-mono">{monitor.target_identifier}</span> · {monitor.entity} ({monitor.direction})
          </>
        }
        onSubmit={showAddForm ? handleSaveWatcher : undefined}
        footer={footer}
      >
        {error && <Callout tone="danger">{error}</Callout>}

        {monitor.composite_explanation && (
          <Callout tone={diagnosisTone(monitor.status)} title="Composite signal diagnosis">
            {monitor.composite_explanation}
          </Callout>
        )}

        {showAddForm ? watcherForm : null}

        <Subsection
          title={`Configured signals (${watchers.length})`}
          actions={
            showAddForm ? null : (
              <button type="button" onClick={openCreateForm} className="button button--secondary button--small">
                <Icon name="plus" />
                Add signal watcher
              </button>
            )
          }
        >
          {watcherList}
        </Subsection>
      </Drawer>

      <ConfirmDialog
        open={watcherToDelete !== null}
        title={`Remove the signal “${watchers.find((w) => w.id === watcherToDelete)?.name ?? ''}”?`}
        description="Commerce Ops stops checking this signal and its history is removed. The integration monitor itself stays."
        confirmLabel="Remove signal"
        danger
        busy={deletingWatcher}
        onConfirm={confirmDeleteWatcher}
        onCancel={() => setWatcherToDelete(null)}
      />
    </>
  );
};
